import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { useId, type FormEvent } from 'react';
import { RoleEnum, type MembershipDetails } from 'api-client';

export type MemberAction = 'role' | 'remove' | null;
interface MemberManagementProps {
  details: MembershipDetails;
  role: RoleEnum;
  pending: MemberAction;
  error: string | null;
  onRole: (role: RoleEnum) => void;
  onSave: () => void;
  onRemove: () => void;
}

export default function MemberManagement({
  details,
  role,
  pending,
  error,
  onRole,
  onSave,
  onRemove,
}: MemberManagementProps) {
  const labelId = useId();
  const member = details.membership;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!pending && details.canEdit && role !== member.role) onSave();
  };
  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Person />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h2" variant="h5" sx={{ overflowWrap: 'anywhere' }}>
              {member.displayName}
            </Typography>
            <Chip
              variant="outlined"
              color={member.role === RoleEnum.Admin ? 'primary' : 'default'}
              label={member.role === RoleEnum.Admin ? 'Administrator' : 'Member'}
              size="small"
            />
            {details.isSelf && <Chip variant="outlined" label="You" size="small" sx={{ ml: 1 }} />}
          </Box>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {details.canEdit ? (
          <Stack
            component="form"
            onSubmit={submit}
            direction="row"
            useFlexGap
            flexWrap="wrap"
            gap={2}
            alignItems="center"
          >
            <FormControl sx={{ flex: '1 1 180px', minWidth: 0 }}>
              <InputLabel id={labelId}>Role</InputLabel>
              <Select<RoleEnum>
                labelId={labelId}
                label="Role"
                value={role}
                disabled={Boolean(pending)}
                onChange={(event) => {
                  const next = event.target.value;
                  if (next === RoleEnum.Admin || next === RoleEnum.Member) onRole(next);
                }}
              >
                <MenuItem value={RoleEnum.Member}>Member</MenuItem>
                <MenuItem value={RoleEnum.Admin}>Administrator</MenuItem>
              </Select>
            </FormControl>
            <Button
              type="submit"
              variant="contained"
              disabled={Boolean(pending) || role === member.role}
              startIcon={pending === 'role' ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {pending === 'role' ? 'Updating…' : 'Update Role'}
            </Button>
          </Stack>
        ) : (
          <Alert severity="info">
            {details.isSelf
              ? 'You cannot change your own role. Another administrator can change it for you.'
              : 'Only team administrators can change member roles.'}
          </Alert>
        )}
        {(details.canRemove || details.isSelf || details.team.isAdmin) && (
          <Stack spacing={2}>
            <Typography component="h3" variant="h6">
              {details.isSelf ? 'Leave Team' : 'Remove Member'}
            </Typography>
            <Typography color="text.secondary">
              {details.isSelf
                ? 'Leaving removes your access to this team.'
                : 'Removing this member removes their access to this team.'}{' '}
              A new invitation can restore access.
            </Typography>
            {details.removalBlockedReason && <Alert severity="warning">{details.removalBlockedReason}</Alert>}
            <Button
              color="error"
              variant="outlined"
              onClick={onRemove}
              disabled={Boolean(pending) || !details.canRemove}
              sx={{ alignSelf: 'flex-start' }}
            >
              {details.isSelf ? 'Leave Team' : 'Remove Member'}
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
